import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Bell, Check, CheckCheck, FileText, Package, ReceiptText, Wrench, CreditCard } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/api";

const CATEGORY_META = {
  jobs: { label: "Jobs", Icon: Wrench, color: "#e63946" },
  parts: { label: "Parts", Icon: Package, color: "#457b9d" },
  estimates: { label: "Estimates", Icon: FileText, color: "#F79009" },
  payments: { label: "Payments", Icon: CreditCard, color: "#12B76A" },
  inventory: { label: "Inventory", Icon: Package, color: "#F79009" },
  system: { label: "System", Icon: Bell, color: "#667085" },
  pickup: { label: "Pickup & Drop", Icon: ReceiptText, color: "#1d3557" },
};

const CATEGORIES = Object.keys(CATEGORY_META);

export function useUnreadCount() {
  const [n, setN] = useState(0);
  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setN(data.unread || 0);
    } catch {
      /* silent */
    }
  }, []);
  useEffect(() => {
    refresh();
    const iv = setInterval(refresh, 60000); // poll every minute
    return () => clearInterval(iv);
  }, [refresh]);
  return { unread: n, refresh };
}

export default function NotificationsDrawer({ open, onOpenChange, onChange }) {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const markRead = async (id) => {
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, read: true } : x)));
    try {
      await api.post(`/notifications/${id}/read`);
      onChange && onChange();
    } catch {
      /* ignore */
    }
  };

  const markAllRead = async () => {
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    try {
      await api.post("/notifications/read-all");
      onChange && onChange();
    } catch {
      /* ignore */
    }
  };

  const visibleItems =
    tab === "all"
      ? items
      : tab === "unread"
      ? items.filter((x) => !x.read)
      : items.filter((x) => x.category === tab);

  const unread = items.filter((x) => !x.read).length;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        aria-describedby={undefined}
        className="w-full sm:w-[420px] p-0 bg-white border-l border-[#E4E7EC]"
        data-testid="notifications-drawer"
      >
        <SheetHeader className="px-5 py-4 border-b border-[#E4E7EC] space-y-0">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-[16px] font-semibold text-[#1d3557] flex items-center gap-2">
              <Bell size={17} className="text-[#457b9d]" />
              Notifications
              {unread > 0 && (
                <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-[#e63946] text-white">
                  {unread}
                </span>
              )}
            </SheetTitle>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                data-testid="notifications-mark-all-read"
                className="text-[12px] font-semibold text-[#457b9d] hover:text-[#e63946] inline-flex items-center gap-1"
              >
                <CheckCheck size={14} /> Mark all read
              </button>
            )}
          </div>
        </SheetHeader>

        {/* Tabs */}
        <div className="px-5 pt-3 border-b border-[#E4E7EC] overflow-x-auto no-scrollbar">
          <div className="inline-flex gap-1 pb-2">
            {[
              { k: "all", label: "All" },
              { k: "unread", label: `Unread${unread ? ` (${unread})` : ""}` },
              ...CATEGORIES.map((c) => ({ k: c, label: CATEGORY_META[c].label })),
            ].map(({ k, label }) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                data-testid={`notif-tab-${k}`}
                className={`px-2.5 py-1 rounded-md text-[12px] font-semibold whitespace-nowrap transition-colors ${
                  tab === k
                    ? "bg-[#1d3557] text-white"
                    : "text-[#667085] hover:text-[#1d3557] hover:bg-[#F1FAEE]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div className="overflow-y-auto flex-1 max-h-[calc(100vh-140px)]">
          {loading && (
            <div className="p-8 text-center text-sm text-[#667085]">Loading…</div>
          )}
          {!loading && visibleItems.length === 0 && (
            <div className="p-10 text-center">
              <Bell size={28} className="mx-auto mb-3 text-[#a8dadc]" />
              <div className="text-[15px] font-semibold text-[#1d3557]">You&apos;re all caught up</div>
              <div className="text-[12px] text-[#667085] mt-1">No new notifications right now.</div>
            </div>
          )}
          {!loading &&
            visibleItems.map((n) => {
              const meta = CATEGORY_META[n.category] || CATEGORY_META.system;
              const { Icon } = meta;
              return (
                <div
                  key={n.id}
                  data-testid={`notif-item-${n.id}`}
                  className={`px-5 py-3 border-b border-[#F1FAEE] flex items-start gap-3 hover:bg-[#F7F9FC] transition-colors ${
                    n.read ? "" : "bg-[#F1FAEE]/60"
                  }`}
                >
                  <div
                    className="w-8 h-8 rounded-lg shrink-0 grid place-items-center"
                    style={{ background: `${meta.color}22`, color: meta.color }}
                  >
                    <Icon size={16} strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div
                        className={`text-[13px] leading-tight ${
                          n.read ? "text-[#667085]" : "font-semibold text-[#1d3557]"
                        }`}
                      >
                        {n.title}
                      </div>
                      {!n.read && (
                        <span className="w-1.5 h-1.5 rounded-full bg-[#e63946] shrink-0" />
                      )}
                    </div>
                    <div className="text-[12px] text-[#667085] mt-0.5 line-clamp-2">
                      {n.body}
                    </div>
                    <div className="text-[10px] text-[#667085] mt-1 flex items-center gap-3">
                      {n.created_at && <span>{fmtDateTime(n.created_at)}</span>}
                      {n.url && (
                        <button
                          className="text-[#457b9d] font-semibold hover:text-[#e63946]"
                          onClick={() => {
                            onOpenChange(false);
                            markRead(n.id);
                            nav(n.url);
                          }}
                          data-testid={`notif-open-${n.id}`}
                        >
                          Open
                        </button>
                      )}
                      {!n.read && (
                        <button
                          className="text-[#457b9d] font-semibold hover:text-[#e63946] inline-flex items-center gap-1"
                          onClick={() => markRead(n.id)}
                          data-testid={`notif-markread-${n.id}`}
                        >
                          <Check size={11} /> Read
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
